import PathAwait from '@/presentation/helpers/page/path-await-page-helper'
import { Box, Button, Container, Typography } from '@mui/material'
import { useLocation, useNavigate } from 'react-router-dom'
import React, { useState, useEffect, useCallback } from 'react'
import { ContactVerificationAnalytics } from './analytics'
import { useLoaderData } from 'react-router-dom'
import CodeInput from './components/code-input'
import ContactVerificationSuccess from './components/contact-verification-success'
import { i18nTranslationHelper } from '@/i18n'
import { contactVerificationService } from '@/data/services/contact-verification/contact-verification-service'
import { AppError } from '@/domain/errors'
import { useLDClient } from 'launchdarkly-react-client-sdk'
import { FeatureFlag } from '@/infra/feature-flag/feature-flag'
import {
  ContactType,
  FormattedUserProfileResponse
} from '@/data/services/contact-verification/contact-verification-service-types'
import { HttpStatusCode } from '@/data/protocols/http'
import { isAllowedToSkipVerification } from '@/data/services/contact-verification/contact-verification-service-utils'
import theme from '@/presentation/assets/styles/theme'

type ValidationTypeProps = 'email' | 'phone'

type ContactVerificationContentProps = {
  locationState: ContactVerificationPageProps
  navigate: ReturnType<typeof useNavigate>
}
export interface ContactVerificationPageProps {
  returnTo?: string
  sendTo?: string
  contactType?: ValidationTypeProps
  nextValidation?: ValidationTypeProps
  targetStep?: number
}

const EXPIRATION_TIME_SECONDS = 150
const RESEND_TIME_SECONDS = 150

export default function ContactVerificationPage() {
  const location = useLocation()
  const navigate = useNavigate()

  useEffect(() => {
    if (!location?.state) {
      console.error(
        'Acesso inválido: dados de verificação ausentes. Redirecionando para a página inicial...'
      )
      navigate('/')
    }

    if (location?.state?.returnTo !== 'onboarding') {
      sessionStorage.setItem('isVerificationPage', 'true')
    }
  }, [location, navigate])

  if (!location?.state) {
    return null
  }

  // key={contactType} força remontagem completa quando muda phone -> email
  return (
    <ContactVerificationContent
      key={location?.state.contactType}
      locationState={location?.state}
      navigate={navigate}
    />
  )
}

function ContactVerificationContent({ locationState, navigate }: ContactVerificationContentProps) {
  const ldClient = useLDClient()
  const userProfile = useLoaderData() as FormattedUserProfileResponse

  const t = { page: i18nTranslationHelper.getTPage }
  const currentContactType = locationState.contactType as ValidationTypeProps
  const isContactValidationEnabled = FeatureFlag.Key.ENABLE_CONTACT_VALIDATION.getValue(ldClient)

  const [inputCode, setInputCode] = useState('')
  const [expirationTimer, setExpirationTimer] = useState(EXPIRATION_TIME_SECONDS)
  const [errorMessage, setErrorMessage] = useState('')
  const [isCounterVisible, setIsCounterVisible] = useState(true)
  const [isCodeExpired, setIsCodeExpired] = useState(false)
  const [isResendEnabled, setIsResendEnabled] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [isVerificationSuccess, setIsVerificationSuccess] = useState(false)
  const [errorCount, setErrorCount] = useState(1)

  const redirectSendTo = locationState?.sendTo || locationState?.returnTo || '/'
  const redirectReturnTo = locationState?.returnTo || '/'
  const normalizedLocation = redirectReturnTo.replace(/\/content\/[^/]+/g, '') || '/'
  const hasTrackedPageView = React.useRef(false)

  const VALIDATION_TYPE = {
    phone: {
      title: t.page('contactVerification.title.phone'),
      description: t.page('contactVerification.subtitle.phone'),
      cta: t.page(`contactVerification.validateButton.phone`),
      successTitle: t.page('contactVerification.successModal.title.phone'),
      successMessage: t.page('contactVerification.successModal.subtitle.phone'),
      contactInfo: userProfile?.phone || '',
      sendCodeTo: 'WHATSAPP'
    },
    email: {
      title: t.page('contactVerification.title.email'),
      description: t.page('contactVerification.subtitle.email'),
      cta: t.page(`contactVerification.validateButton.email`),
      successTitle: t.page('contactVerification.successModal.title.email'),
      successMessage: t.page('contactVerification.successModal.subtitle.email'),
      contactInfo: userProfile?.personalEmail || '',
      sendCodeTo: 'EMAIL'
    }
  }

  useEffect(() => {
    if (!isContactValidationEnabled) {
      handleRedirect(redirectReturnTo)
      return
    }

    if (
      (currentContactType == 'email' && !userProfile?.personalEmail) ||
      (currentContactType == 'phone' && !userProfile?.phone)
    ) {
      handleRedirect(redirectReturnTo)
      return
    }

    if (
      (currentContactType == 'email' && userProfile?.verifiedPersonalEmail) ||
      (currentContactType == 'phone' && userProfile?.verifiedPhone)
    ) {
      handleRedirect(redirectSendTo)
      return
    }

    handleSendCode()
  }, [])

  useEffect(() => {
    if (!hasTrackedPageView.current) {
      ContactVerificationAnalytics.trackPageView(normalizedLocation, currentContactType)
      hasTrackedPageView.current = true
    }
  }, [])

  useEffect(() => {
    if (expirationTimer === 0) {
      setIsCodeExpired(true)
      setIsCounterVisible(false)
      return
    }
    const timer = setTimeout(() => setExpirationTimer(s => s - 1), 1000)
    return () => clearTimeout(timer)
  }, [expirationTimer])

  const isSkipButtonVisible = isAllowedToSkipVerification(
    userProfile,
    currentContactType as ValidationTypeProps
  )

  const handleSendCode = useCallback(async () => {
    setInputCode('')
    try {
      const contactType = VALIDATION_TYPE[currentContactType].sendCodeTo as ContactType
      const result = await contactVerificationService.sendCode(contactType)
      if (!result?.success) {
        setErrorMessage(t.page('contactVerification.errors.serverError'))
        return
      }
      setIsResendEnabled(false)

      setTimeout(() => {
        setIsResendEnabled(true)
      }, RESEND_TIME_SECONDS * 1000)

      setExpirationTimer(EXPIRATION_TIME_SECONDS)
      setIsCounterVisible(true)
      setIsCodeExpired(false)
      setErrorMessage('')
    } catch (err) {
      setErrorMessage(t.page('contactVerification.errors.serverError'))
    }
  }, [currentContactType])

  const handleCodeValidation = useCallback(async () => {
    setIsLoading(true)
    setErrorMessage('')

    ContactVerificationAnalytics.trackValidate(normalizedLocation, currentContactType)
    try {
      await contactVerificationService.verifyCode(inputCode)

      setIsVerificationSuccess(true)

      ContactVerificationAnalytics.trackConfirmSuccess(normalizedLocation, currentContactType)
      ContactVerificationAnalytics.trackSuccessView(normalizedLocation, currentContactType)
    } catch (err) {
      const isBadRequest = (err as AppError).statusCode === HttpStatusCode.badRequest

      setErrorCount(prev => prev + 1)

      ContactVerificationAnalytics.trackConfirmError(
        normalizedLocation,
        currentContactType,
        errorCount.toString(),
        isBadRequest ? 'invalidOrExpiredCode' : 'serverError'
      )

      if (isBadRequest) {
        setErrorMessage(t.page('contactVerification.errors.invalidCode'))
        setInputCode('')
      } else {
        setErrorMessage(t.page('contactVerification.errors.serverError'))
      }
    } finally {
      setIsLoading(false)
    }
  }, [inputCode])

  const handleRedirect = (path = '/') => {
    navigate(path)
  }

  const handleSuccessContinue = () => {
    if (isVerificationSuccess && locationState?.nextValidation) {
      const navState = {
        returnTo: redirectReturnTo,
        sendTo: redirectSendTo,
        contactType: locationState.nextValidation,
        nextValidation: null,
        targetStep: locationState.targetStep
      }
      navigate('/contact-verification', { state: navState, replace: true })
      return
    }

    if (isVerificationSuccess && locationState?.sendTo) {
      const sendToState = locationState?.targetStep
        ? {
            targetStep: locationState.targetStep
          }
        : null
      navigate(redirectSendTo, { state: sendToState })
      return
    }

    navigate(redirectReturnTo)
  }

  return (
    <React.Fragment>
      <PathAwait>
        <React.Fragment>
          {userProfile ? (
            <Container
              maxWidth="xs"
              sx={{
                display: 'flex',
                flex: 1,
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                textAlign: 'left',
                width: '100%',
                py: 4
              }}
            >
              <Box
                sx={{
                  border: '1px solid #ddd',
                  background: '#fff',
                  borderRadius: theme.spacing(3 / 2),
                  p: { xs: 2, sm: 4 },
                  gap: 3,
                  display: 'flex',
                  flexDirection: 'column',
                  width: '100%'
                }}
              >
                {isVerificationSuccess ? (
                  <ContactVerificationSuccess
                    title={VALIDATION_TYPE[currentContactType]?.successTitle}
                    message={VALIDATION_TYPE[currentContactType]?.successMessage}
                    onContinue={() => {
                      ContactVerificationAnalytics.trackSuccessContinue(
                        normalizedLocation,
                        currentContactType
                      )
                      handleSuccessContinue()
                    }}
                  />
                ) : (
                  <React.Fragment>
                    <Box className="flex flex-col items-center w-full gap-2">
                      <Typography variant="h6" className="text-center" sx={{ fontWeight: 700 }}>
                        {VALIDATION_TYPE[currentContactType]?.title}
                      </Typography>

                      <Typography variant="body2" className="text-center">
                        {VALIDATION_TYPE[currentContactType]?.description}
                        <b style={{ fontWeight: 700 }}>
                          {VALIDATION_TYPE[currentContactType]?.contactInfo}
                        </b>
                      </Typography>
                    </Box>

                    <Box className="flex flex-col items-center w-full">
                      <CodeInput
                        value={inputCode}
                        onChange={setInputCode}
                        error={!!errorMessage}
                        disabled={isLoading || isCodeExpired}
                        onEnter={() => {
                          if (inputCode.length === 6) handleCodeValidation()
                        }}
                      />
                    </Box>

                    <Box className="flex flex-col items-center w-full">
                      {isCounterVisible && (
                        <Typography variant="body2" className="text-center">
                          {t.page('contactVerification.codeValidFor', {
                            seconds: `${expirationTimer}`
                          })}
                        </Typography>
                      )}

                      {isCodeExpired && (
                        <Typography variant="body2" color="error" className="text-center">
                          {t.page('contactVerification.errors.codeExpired')}
                        </Typography>
                      )}

                      {errorMessage && (
                        <Typography variant="body2" color="error" className="text-center">
                          {errorMessage}
                        </Typography>
                      )}

                      <Button
                        variant="text"
                        color="primary"
                        onClick={() => {
                          handleSendCode()
                          ContactVerificationAnalytics.trackResend(
                            normalizedLocation,
                            currentContactType
                          )
                        }}
                        disabled={!isResendEnabled}
                        aria-label="resend-code"
                      >
                        {t.page('contactVerification.resendCode')}
                      </Button>
                    </Box>

                    <Box className="flex flex-col items-center w-full gap-4">
                      <Button
                        variant="contained"
                        color="primary"
                        className="w-full"
                        disabled={inputCode.length < 6 || isLoading || isCodeExpired}
                        onClick={handleCodeValidation}
                      >
                        {t.page(`contactVerification.validateButton.${currentContactType}`)}
                      </Button>

                      {isSkipButtonVisible && (
                        <Button
                          variant="outlined"
                          color="inherit"
                          className="w-full"
                          onClick={() => {
                            ContactVerificationAnalytics.trackSkip(
                              normalizedLocation,
                              currentContactType
                            )

                            handleRedirect(redirectSendTo)
                          }}
                        >
                          {t.page('contactVerification.skipButton')}
                        </Button>
                      )}

                      <Button
                        variant="text"
                        className="w-full"
                        color="inherit"
                        onClick={() => handleRedirect(redirectReturnTo)}
                      >
                        {t.page('contactVerification.backButton')}
                      </Button>
                    </Box>
                  </React.Fragment>
                )}
              </Box>
            </Container>
          ) : null}
        </React.Fragment>
      </PathAwait>
    </React.Fragment>
  )
}
